import { CARD_SURFACE } from '@/components/ui/section-card-variants'
import { cn } from '@/lib/utils'

/*
 * The Overview blocks' own boxes, shared by each loaded block and its loading shape in
 * `OverviewBoardStates` so the two cannot drift. A `.ts` module so a component file exports only
 * components.
 */

/** The floor stats panel (mock `bra-ov-stats`): the card fill, no border, four cells. */
export const FLOOR_STATS_PANEL = cn('@container min-w-0', CARD_SURFACE)

/** One row of four from `@2xl` of the strip, a 2×2 grid below it. */
export const FLOOR_STATS_GRID = 'grid grid-cols-2 @2xl:grid-cols-4'

/** A cell: 12 above and below, 16 in from the left, 12 from the right (the mock's 64 tall). */
export const FLOOR_CELL = 'flex min-w-0 flex-col py-3 pr-3 pl-4'

/** A subtle hairline before every cell that has a neighbour on that side. */
export const floorCellRules = (index: number): string =>
  cn(
    'border-border-subtle',
    index % 2 === 1 ? 'border-l' : null,
    index >= 2 ? 'border-t @2xl:border-t-0' : null,
    index === 0 ? null : '@2xl:border-l',
  )

/**
 * A Top brokers row (mock `bra-ov-bar`): 28 tall, broker 168 (mock 170) · track · value 80, 16
 * apart, bleeding the card's 20px side padding so the hover fill reaches its edges. Below `@lg`
 * of the card the broker cell narrows to 124 and its trailing code goes, as the mock's phone row.
 */
export const TOP_BROKER_ROW = '-mx-5 flex h-7 items-center gap-4 px-5'
export const TOP_BROKER_CELL = 'flex w-31 min-w-0 shrink-0 items-center @lg:w-42'
export const TOP_BROKER_VALUE = 'w-16 shrink-0 text-right @lg:w-20'

/**
 * Sectors traded most beside Stocks traded most (mock `bra-tp-pair`): 1.25fr / 1fr, 24 apart,
 * both tables stretched to the taller one; stacked below `@3xl` of the main column.
 */
export const TRADED_PAIR = 'grid grid-cols-1 gap-6 @3xl:grid-cols-[minmax(0,5fr)_minmax(0,4fr)]'

/** A traded-most table's bordered card (mock `bra-tt__table`): card fill, default hairline. */
export const TRADED_TABLE = 'flex flex-1 flex-col rounded-lg border border-border bg-card'

/** Its head band: the table-head tint, 12/16 tertiary, the default hairline under it. */
export const TRADED_HEAD =
  'grid items-center gap-x-6 border-b border-border bg-(--table-head) px-4 pt-3 pb-2 text-body-xs text-muted-foreground-subtle'

/** Sector · Share of turnover · Turnover (mock `1fr / 96–128 / 84`). */
export const SECTOR_COLUMNS =
  'grid-cols-[minmax(0,1fr)_minmax(--spacing(24),--spacing(32))_--spacing(21)]'

/** # · Bought most · Sold most. */
export const STOCK_COLUMNS = 'grid-cols-[--spacing(4)_minmax(0,1fr)_minmax(0,1fr)]'
